/**
 * API for fetching public share data
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.familywaffle.com';

export interface PublicShareResponse {
  post: {
    id: string;
    caption_ct: string;
    caption_nonce: string;
    post_date: string | null;
    created_at: string;
    owner: {
      name: string;
      profile_picture_url: string | null;
    };
  };
  assets: Array<{
    id: string;
    kind: 'image' | 'video' | 'audio' | 'thumbnail';
    mime: string;
    width: number | null;
    height: number | null;
    duration_ms: number | null;
    download_url: string;
    thumb_download_url: string | null;
    cek_wrap: string;
    cek_wrap_nonce: string;
    asset_nonce: string;
    nonce_scheme: 'single' | 'segmented';
    segment_size: number | null;
    ct_sha256: string;
    display_order: number;
    detail_ct: string | null;
    detail_nonce: string | null;
    /**
     * Voice details are audio assets, which this viewer does not decrypt — a
     * photo whose detail is a recording simply shows no detail here.
     */
    detail_voice_asset_id: string | null;
    detail_voice_duration_ms: number | null;
  }>;
}

export interface ShareError {
  type: 'not_found' | 'revoked' | 'network' | 'unknown';
  message: string;
  revokedAt?: string;
}

/**
 * Fetch public share data from Hono API
 */
export async function fetchPublicShare(shareId: string): Promise<PublicShareResponse> {
  const url = `${API_BASE_URL}/s/${encodeURIComponent(shareId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw {
        type: 'not_found',
        message: 'This share does not exist',
      } as ShareError;
    }

    if (response.status === 410) {
      const data = await response.json().catch(() => ({}));
      throw {
        type: 'revoked',
        message: 'This share has been revoked by the owner',
        revokedAt: data.error?.revoked_at,
      } as ShareError;
    }

    throw {
      type: 'network',
      message: `Server error: ${response.status}`,
    } as ShareError;
  }

  // Hono API wraps response in { success, data, requestId }
  const json = await response.json();
  if (json.success && json.data) {
    return json.data as PublicShareResponse;
  }

  throw {
    type: 'unknown',
    message: 'Invalid response format',
  } as ShareError;
}

/**
 * Download encrypted asset from presigned URL
 */
export async function downloadEncryptedAsset(url: string): Promise<Uint8Array> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to download asset: ${response.status}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}
